import React from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './icons'
import { DataState, MCAvatar } from './shared'
import { fmtRelTime } from './format'
import { isApiError } from './api'
import { InventoryEditorModal } from './edit_inventory'
import { bareId, derivedBlockIconUrl, effectLabel, enchantmentLabel, itemIconUrl, blockIconUrl, overrideIcon, parseEnchantment, prettyId, renderChestIconUrl, ticksToClock, toRoman } from './mcAssets'
import { PeoplePage } from './pages_people'
import type { ApiError, InventoryEditRequest, InventoryItem, ItemRegistry, PeopleState, PersonKind, PlayerSnapshot, Role, Server, ShulkerContents } from './types'

type SubTab = 'players' | 'access'

interface PlayersTabProps {
  server: Server
  role: Role
  onFetchLive: (signal?: AbortSignal) => Promise<PlayerSnapshot[]>
  onFetchOffline: (signal?: AbortSignal) => Promise<PlayerSnapshot[]>
  canEditInventory: boolean
  onFetchRegistry: (signal?: AbortSignal) => Promise<ItemRegistry>
  onEditInventory: (playerName: string, body: InventoryEditRequest) => Promise<unknown>
  onFetchShulker: (playerName: string, slot: number) => Promise<ShulkerContents>
  onEditShulker: (playerName: string, slot: number, body: InventoryEditRequest) => Promise<unknown>
  onFetchPeople: () => Promise<PeopleState>
  onMutatePeople: (
    kind: PersonKind,
    action: 'add' | 'remove',
    body: { name: string; reason?: string | null },
    verb: string,
  ) => Promise<boolean>
}

const SUB_TABS: { key: SubTab; label: string }[] = [
  { key: 'players', label: 'Players' },
  { key: 'access', label: 'Access' },
]

export function PlayersTab({
  server,
  role,
  onFetchLive,
  onFetchOffline,
  canEditInventory,
  onFetchRegistry,
  onEditInventory,
  onFetchShulker,
  onEditShulker,
  onFetchPeople,
  onMutatePeople,
}: PlayersTabProps) {
  const [sub, setSub] = React.useState<SubTab>('players')

  return (
    <div>
      <div className="seg-radio" style={{ marginBottom: 14 }}>
        {SUB_TABS.map(tab => (
          <button
            key={tab.key}
            className={sub === tab.key ? 'active' : ''}
            onClick={() => setSub(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {sub === 'players' && (
        <PlayersView
          server={server}
          role={role}
          onFetchLive={onFetchLive}
          onFetchOffline={onFetchOffline}
          canEditInventory={canEditInventory}
          onFetchRegistry={onFetchRegistry}
          onEditInventory={onEditInventory}
          onFetchShulker={onFetchShulker}
          onEditShulker={onEditShulker}
        />
      )}
      {sub === 'access' && (
        <PeoplePage
          serverId={server.id}
          serverName={server.name}
          role={role}
          onFetch={onFetchPeople}
          onMutate={onMutatePeople}
        />
      )}
    </div>
  )
}


const PLAYERS_POLL_MS = 2_000
const OFFLINE_PAGE_SIZE = 20


interface PlayersViewProps {
  server: Server
  role: Role
  onFetchLive: (signal?: AbortSignal) => Promise<PlayerSnapshot[]>
  onFetchOffline: (signal?: AbortSignal) => Promise<PlayerSnapshot[]>
  canEditInventory: boolean
  onFetchRegistry: (signal?: AbortSignal) => Promise<ItemRegistry>
  onEditInventory: (playerName: string, body: InventoryEditRequest) => Promise<unknown>
  onFetchShulker: (playerName: string, slot: number) => Promise<ShulkerContents>
  onEditShulker: (playerName: string, slot: number, body: InventoryEditRequest) => Promise<unknown>
}

function PlayersView({
  server,
  role,
  onFetchLive,
  onFetchOffline,
  canEditInventory,
  onFetchRegistry,
  onEditInventory,
  onFetchShulker,
  onEditShulker,
}: PlayersViewProps) {
  const [live, setLive] = React.useState<PlayerSnapshot[]>([])
  const [offline, setOffline] = React.useState<PlayerSnapshot[]>([])
  const [loading, setLoading] = React.useState(true)
  const [hasLoaded, setHasLoaded] = React.useState(false)
  const [error, setError] = React.useState<ApiError | null>(null)
  const [visibleOffline, setVisibleOffline] = React.useState(OFFLINE_PAGE_SIZE)
  const [editor, setEditor] = React.useState<PlayerSnapshot | null>(null)
  const [registry, setRegistry] = React.useState<ItemRegistry | null>(null)
  const [registryLoading, setRegistryLoading] = React.useState(false)
  const [registryError, setRegistryError] = React.useState<string | null>(null)
  const liveRef = React.useRef(onFetchLive)
  const offlineRef = React.useRef(onFetchOffline)
  const registryRef = React.useRef(onFetchRegistry)

  React.useEffect(() => { liveRef.current = onFetchLive }, [onFetchLive])
  React.useEffect(() => { offlineRef.current = onFetchOffline }, [onFetchOffline])
  React.useEffect(() => { registryRef.current = onFetchRegistry }, [onFetchRegistry])

  const serverIsUp = server.status === 'online'

  const load = React.useCallback(async (signal?: AbortSignal) => {
    const livePromise = serverIsUp ? liveRef.current(signal) : Promise.resolve<PlayerSnapshot[]>([])
    const offlinePromise = offlineRef.current(signal)
    const [liveRes, offlineRes] = await Promise.allSettled([livePromise, offlinePromise])
    if (signal?.aborted) return

    if (liveRes.status === 'fulfilled') {
      setLive(liveRes.value)
    } else if (!serverIsUp) {
      setLive([])
    }
    // If the live call failed while the server is up, keep the previous list
    // so a transient 503 doesn't blank the UI between polls.

    if (offlineRes.status === 'fulfilled') {
      setOffline(offlineRes.value)
      setError(null)
    } else {
      const err = offlineRes.reason
      if (err && typeof err === 'object' && 'name' in err && (err as { name?: string }).name === 'AbortError') return
      if (isApiError(err)) setError(err)
      else setError({ status: 0, message: 'Network error' })
    }

    setLoading(false)
    setHasLoaded(true)
  }, [serverIsUp])

  React.useEffect(() => {
    setLoading(true)
    setHasLoaded(false)
    setVisibleOffline(OFFLINE_PAGE_SIZE)
    const controller = new AbortController()
    void load(controller.signal)
    const interval = window.setInterval(() => {
      if (!controller.signal.aborted) void load(controller.signal)
    }, PLAYERS_POLL_MS)
    return () => {
      controller.abort()
      window.clearInterval(interval)
    }
  }, [server.id, serverIsUp, load])

  const openEditor = React.useCallback((snapshot: PlayerSnapshot) => {
    setEditor(snapshot)
    if (registry || registryLoading) return
    setRegistryLoading(true)
    setRegistryError(null)
    void registryRef.current()
      .then(data => setRegistry(data))
      .catch(err => {
        if (isApiError(err)) setRegistryError(err.message)
        else setRegistryError('Registry unavailable')
      })
      .finally(() => setRegistryLoading(false))
  }, [registry, registryLoading])

  const saveInventory = React.useCallback(async (playerName: string, body: InventoryEditRequest): Promise<boolean> => {
    const result = await onEditInventory(playerName, body)
    if (!result) return false
    await load()
    return true
  }, [onEditInventory, load])

  const fetchShulker = React.useCallback(async (playerName: string, slot: number): Promise<ShulkerContents | null> => {
    try {
      return await onFetchShulker(playerName, slot)
    } catch {
      return null
    }
  }, [onFetchShulker])

  const saveShulker = React.useCallback(async (playerName: string, slot: number, body: InventoryEditRequest): Promise<boolean> => {
    const result = await onEditShulker(playerName, slot, body)
    if (!result) return false
    await load()
    return true
  }, [onEditShulker, load])

  const onlineNames = new Set(live.map(s => s.name.toLowerCase()))
  const sortedOnline = [...live].sort((a, b) => a.name.localeCompare(b.name))
  const sortedOffline = offline
    .filter(s => !onlineNames.has(s.name.toLowerCase()))
    .sort((a, b) => (b.savedAt ?? '').localeCompare(a.savedAt ?? ''))
  const visibleOfflineList = sortedOffline.slice(0, visibleOffline)
  const hasMoreOffline = sortedOffline.length > visibleOfflineList.length

  return (
    <DataState
      loading={loading && !hasLoaded}
      error={error}
      onRetry={() => { setLoading(true); setHasLoaded(false); void load() }}
    >
      <div className="flex-col" style={{ gap: 28 }}>
        <section className="flex-col gap-3">
          <div className="mc-player-group-head">
            <span className="mc-player-status-dot online" />
            <span className="mc-player-group-title">Online</span>
            <span className="dim">· {sortedOnline.length}</span>
            {!serverIsUp && <span className="dim text-xs" style={{ marginLeft: 8 }}>Server is offline</span>}
          </div>
          {sortedOnline.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '24px 20px' }}>
              <Icon name={serverIsUp ? 'users' : 'power'} size={22} style={{ opacity: 0.4, marginBottom: 6 }} />
              <div className="dim text-sm">
                {serverIsUp ? 'No players online' : 'Start the server to see live player state.'}
              </div>
            </div>
          ) : (
            sortedOnline.map(snap => (
              <PlayerSnapshotCard
                key={`on-${snap.uuid ?? snap.name}`}
                snapshot={snap}
                role={role}
                canEditInventory={canEditInventory}
                onEditInventory={openEditor}
              />
            ))
          )}
        </section>

        <section className="flex-col gap-3">
          <div className="mc-player-group-head">
            <span className="mc-player-status-dot offline" />
            <span className="mc-player-group-title">Offline</span>
            <span className="dim">· {sortedOffline.length}</span>
          </div>
          {sortedOffline.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '24px 20px' }}>
              <Icon name="archive" size={22} style={{ opacity: 0.4, marginBottom: 6 }} />
              <div className="dim text-sm">No saved player data. Players who join and then leave will appear here.</div>
            </div>
          ) : (
            <>
              {visibleOfflineList.map(snap => (
                <PlayerSnapshotCard
                  key={`off-${snap.uuid ?? snap.name}`}
                  snapshot={snap}
                  role={role}
                  canEditInventory={false}
                  onEditInventory={openEditor}
                />
              ))}
              {hasMoreOffline && (
                <button className="btn ghost" onClick={() => setVisibleOffline(c => c + OFFLINE_PAGE_SIZE)}>
                  <Icon name="chevron-down" size={12} /> Load more ({sortedOffline.length - visibleOfflineList.length} remaining)
                </button>
              )}
            </>
          )}
        </section>
      </div>
      {editor && (
        <InventoryEditorModal
          snapshot={editor}
          registry={registry}
          registryLoading={registryLoading}
          registryError={registryError}
          onClose={() => setEditor(null)}
          onSave={body => saveInventory(editor.name, body)}
          onFetchShulker={slot => fetchShulker(editor.name, slot)}
          onSaveShulker={(slot, body) => saveShulker(editor.name, slot, body)}
        />
      )}
    </DataState>
  )
}


function PlayerSnapshotCard({
  snapshot,
  role,
  canEditInventory = false,
  onEditInventory,
}: {
  snapshot: PlayerSnapshot
  role: Role
  canEditInventory?: boolean
  onEditInventory?: (snapshot: PlayerSnapshot) => void
}) {
  const [x, y, z] = snapshot.position
  const isPartial = snapshot.partial === true
  const canViewGamemode = role === 'owner' && Boolean(snapshot.gamemode)
  return (
    <div className="card mc-player-card">
      <div className="mc-player-head">
        <MCAvatar name={snapshot.name} size="md" uuid={snapshot.uuid} />
        <div className="mc-player-id">
          <div className="mc-player-name mono">{snapshot.name}</div>
          <div className="mc-player-meta text-xs dim">
            {isPartial ? (
              <>
                <span>Online</span>
                <span>&middot;</span>
                <span>Live details unavailable</span>
              </>
            ) : (
              <>
                {canViewGamemode && (
                  <>
                    <span>{capitalize(snapshot.gamemode ?? '')}</span>
                    <span>&middot;</span>
                  </>
                )}
                <span>{bareId(snapshot.dimension).replace(/_/g, ' ')}</span>
                <span>&middot;</span>
                <span className="mono">{x.toFixed(0)} {y.toFixed(0)} {z.toFixed(0)}</span>
              </>
            )}
          </div>
        </div>
        <div className="mc-player-tags">
          {snapshot.source === 'live' && canEditInventory && !isPartial && (
            <button className="btn sm ghost mc-player-edit-inv-btn" onClick={() => onEditInventory?.(snapshot)} title="Edit inventory" aria-label="Edit inventory">
              <Icon name="sliders" size={12} /> <span className="mc-player-edit-inv-label">Inventory</span>
            </button>
          )}
          {snapshot.source === 'live' ? (
            <span className={`mc-player-status online${role === 'owner' ? ' compact' : ''}`} title={snapshot.error ?? undefined}>
              <span className="mc-player-status-dot online" /> <span className="mc-player-status-label">Online</span>
            </span>
          ) : (
            <span className="mc-player-status offline" title={snapshot.savedAt ?? ''}>
              <span className="mc-player-status-dot offline" /> Offline · {fmtRelTime(snapshot.savedAt ?? undefined)}
            </span>
          )}
        </div>
      </div>

      {isPartial ? (
        <div className="mc-player-partial">
          <Icon name="alert-triangle" size={16} />
          <div>
            <strong>Inventory snapshot failed.</strong>
            <div className="dim text-xs mono">{snapshot.error ?? 'Retrying on the next poll.'}</div>
          </div>
        </div>
      ) : (
        <>
          <div className="mc-player-bars">
            <HeartRow health={snapshot.health} maxHealth={snapshot.maxHealth} absorption={snapshot.absorption} />
            <FoodRow food={snapshot.food} />
            <XpBar level={snapshot.xpLevel} progress={snapshot.xpProgress} />
          </div>

          {snapshot.effects.length > 0 && (
            <div className="mc-player-effects">
              {snapshot.effects.map((e, idx) => (
                <span key={idx} className="chip mc-effect-chip">
                  <strong>{effectLabel(e.id)}</strong>
                  {e.amplifier > 0 && <> {toRoman(e.amplifier)}</>}
                  <span className="dim" style={{ marginLeft: 6 }}>{ticksToClock(e.durationTicks)}</span>
                </span>
              ))}
            </div>
          )}

          <div className="mc-inv-layout">
            <ArmorColumn armor={snapshot.armor} />
            <MainInventory inventory={snapshot.inventory} />
            <OffhandColumn offhand={snapshot.offhand} />
          </div>
        </>
      )}
    </div>
  )
}


function HeartRow({ health, maxHealth, absorption }: { health: number; maxHealth: number; absorption: number }) {
  const total = Math.max(10, Math.ceil(maxHealth / 2))
  const hearts: React.ReactNode[] = []
  for (let i = 0; i < total; i++) {
    const heartHealth = Math.max(0, Math.min(2, health - i * 2))
    const kind = heartHealth >= 2 ? 'full' : heartHealth >= 1 ? 'half' : 'empty'
    hearts.push(<Heart key={i} kind={kind} />)
  }
  return (
    <div className="mc-bar-row" aria-label={`Health ${health} / ${maxHealth}`}>
      <div className="mc-icon-row">{hearts}</div>
      <span className="mono text-xs dim">{health.toFixed(1)} / {maxHealth.toFixed(0)}</span>
      {absorption > 0 && (
        <span className="chip" style={{ marginLeft: 4, color: '#fbbf24' }}>+{absorption.toFixed(0)} abs</span>
      )}
    </div>
  )
}


function FoodRow({ food }: { food: number }) {
  const drumsticks: React.ReactNode[] = []
  for (let i = 0; i < 10; i++) {
    const v = Math.max(0, Math.min(2, food - i * 2))
    const kind = v >= 2 ? 'full' : v >= 1 ? 'half' : 'empty'
    drumsticks.push(<Drumstick key={i} kind={kind} />)
  }
  return (
    <div className="mc-bar-row" aria-label={`Food ${food} / 20`}>
      <div className="mc-icon-row">{drumsticks}</div>
      <span className="mono text-xs dim">{food} / 20</span>
    </div>
  )
}


function XpBar({ level, progress }: { level: number; progress: number }) {
  return (
    <div className="mc-bar-row" aria-label={`XP level ${level}`}>
      <span className="mc-xp-level mono">{level}</span>
      <div className="mc-xp-track">
        <div className="mc-xp-fill" style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }} />
      </div>
      <span className="mono text-xs dim">{(progress * 100).toFixed(0)}%</span>
    </div>
  )
}


function Heart({ kind }: { kind: 'full' | 'half' | 'empty' }) {
  const full = '#dc2626'
  const empty = '#3f3f46'
  const base = (
    <path
      d="M8 14.5l-5.2-5.2a3.5 3.5 0 010-4.95 3.5 3.5 0 014.95 0L8 5.6l.25-.25a3.5 3.5 0 014.95 0 3.5 3.5 0 010 4.95L8 14.5z"
      fill={empty}
    />
  )
  return (
    <svg className="mc-heart" viewBox="0 0 16 16" width={14} height={14}>
      {base}
      {kind !== 'empty' && (
        <path
          d="M8 14.5l-5.2-5.2a3.5 3.5 0 010-4.95 3.5 3.5 0 014.95 0L8 5.6l.25-.25a3.5 3.5 0 014.95 0 3.5 3.5 0 010 4.95L8 14.5z"
          fill={full}
          clipPath={kind === 'half' ? 'inset(0 50% 0 0)' : undefined}
        />
      )}
    </svg>
  )
}


function Drumstick({ kind }: { kind: 'full' | 'half' | 'empty' }) {
  const full = '#c2833d'
  const empty = '#3f3f46'
  const shape = (fill: string, clip?: string) => (
    <path
      d="M3 10c0-2.2 1.8-4 4-4s4 1.8 4 4c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1zm3 1v2c0 .6.4 1 1 1s1-.4 1-1v-2H6z"
      fill={fill}
      clipPath={clip}
    />
  )
  return (
    <svg className="mc-drumstick" viewBox="0 0 16 16" width={14} height={14}>
      {shape(empty)}
      {kind !== 'empty' && shape(full, kind === 'half' ? 'inset(0 50% 0 0)' : undefined)}
    </svg>
  )
}


function ArmorColumn({ armor }: { armor: InventoryItem[] }) {
  // Slots 103, 102, 101, 100 = helmet, chest, legs, boots (top-down)
  const bySlot = new Map<number, InventoryItem>(armor.map(item => [item.slot, item]))
  const order = [103, 102, 101, 100]
  return (
    <div className="mc-armor-col">
      {order.map(slot => (
        <Slot key={slot} item={bySlot.get(slot)} placeholder={ARMOR_PLACEHOLDER[slot]} />
      ))}
    </div>
  )
}


function OffhandColumn({ offhand }: { offhand: InventoryItem | null | undefined }) {
  return (
    <div className="mc-offhand-col">
      <Slot item={offhand ?? undefined} placeholder="OH" />
    </div>
  )
}


function MainInventory({ inventory }: { inventory: InventoryItem[] }) {
  const bySlot = new Map<number, InventoryItem>(inventory.map(item => [item.slot, item]))
  const rows: number[][] = [
    [9, 10, 11, 12, 13, 14, 15, 16, 17],
    [18, 19, 20, 21, 22, 23, 24, 25, 26],
    [27, 28, 29, 30, 31, 32, 33, 34, 35],
  ]
  const hotbar = [0, 1, 2, 3, 4, 5, 6, 7, 8]
  return (
    <div className="mc-inv-col">
      <div className="mc-inv-grid">
        {rows.flat().map(slot => <Slot key={slot} item={bySlot.get(slot)} />)}
      </div>
      <div className="mc-inv-grid mc-hotbar">
        {hotbar.map(slot => <Slot key={slot} item={bySlot.get(slot)} />)}
      </div>
    </div>
  )
}


const ARMOR_PLACEHOLDER: Record<number, string> = {
  100: 'B',
  101: 'L',
  102: 'C',
  103: 'H',
}


const isHoverCapableDevice = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches

function Slot({ item, placeholder }: { item?: InventoryItem; placeholder?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [anchor, setAnchor] = React.useState<DOMRect | null>(null)
  const hoverCapable = React.useMemo(isHoverCapableDevice, [])

  React.useEffect(() => {
    if (hoverCapable || !anchor) return
    const onDocPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAnchor(null)
      }
    }
    document.addEventListener('pointerdown', onDocPointerDown)
    return () => document.removeEventListener('pointerdown', onDocPointerDown)
  }, [hoverCapable, anchor])

  if (!item) {
    return (
      <div className="mc-inv-slot empty" aria-label="empty slot">
        {placeholder && <span className="mc-slot-placeholder">{placeholder}</span>}
      </div>
    )
  }

  const openTooltip = () => {
    if (ref.current) setAnchor(ref.current.getBoundingClientRect())
  }
  const closeTooltip = () => setAnchor(null)
  const toggleTooltip = () => {
    if (anchor) setAnchor(null)
    else if (ref.current) setAnchor(ref.current.getBoundingClientRect())
  }

  const handlers = hoverCapable
    ? { onMouseEnter: openTooltip, onMouseLeave: closeTooltip }
    : { onClick: toggleTooltip }

  return (
    <div
      ref={ref}
      className="mc-inv-slot"
      aria-label={describeItem(item)}
      {...handlers}
    >
      <ItemIcon id={item.id} />
      {item.count > 1 && <span className="mc-slot-count mono">{item.count}</span>}
      {item.damage != null && item.damage > 0 && <DurabilityBar damage={item.damage} />}
      {anchor && <ItemTooltip item={item} anchor={anchor} />}
    </div>
  )
}


function ItemTooltip({ item, anchor }: { item: InventoryItem; anchor: DOMRect }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(null)

  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const margin = 6
    const vw = window.innerWidth
    const vh = window.innerHeight

    let left = anchor.left + anchor.width / 2 - rect.width / 2
    let top = anchor.top - rect.height - margin
    if (top < margin) top = anchor.bottom + margin
    if (left < margin) left = margin
    if (left + rect.width > vw - margin) left = vw - margin - rect.width
    if (top + rect.height > vh - margin) top = Math.max(margin, vh - margin - rect.height)

    setPos({ left, top })
  }, [anchor])

  const enchantments = item.enchantments.map(parseEnchantment).filter(e => e.id.length > 0)
  const hasMeta = item.count > 1 || (item.damage != null && item.damage > 0)

  return createPortal(
    <div
      ref={ref}
      className="mc-tooltip"
      role="tooltip"
      style={{
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div className={`mc-tooltip-name${item.customName ? ' custom' : ''}`}>
        {item.customName || prettyId(item.id)}
      </div>
      {item.customName && (
        <div className="mc-tooltip-subname">{prettyId(item.id)}</div>
      )}
      {enchantments.map((ench, idx) => (
        <div key={idx} className="mc-tooltip-ench">
          {enchantmentLabel(ench.id)}
          {ench.level > 0 && <> {toRoman(ench.level - 1)}</>}
        </div>
      ))}
      {hasMeta && (
        <div className="mc-tooltip-meta">
          {item.count > 1 && <span>x{item.count}</span>}
          {item.count > 1 && item.damage != null && item.damage > 0 && <span>{' · '}</span>}
          {item.damage != null && item.damage > 0 && <span>Damage {item.damage}</span>}
        </div>
      )}
      <div className="mc-tooltip-id mono">{item.id}</div>
    </div>,
    document.body,
  )
}


function describeItem(item: InventoryItem): string {
  const lines: string[] = [item.customName || prettyId(item.id)]
  if (item.customName) lines.push(prettyId(item.id))
  if (item.count > 1) lines.push(`x${item.count}`)
  if (item.damage != null && item.damage > 0) lines.push(`Damage ${item.damage}`)
  if (item.enchantments.length) {
    for (const raw of item.enchantments) {
      const { id, level } = parseEnchantment(raw)
      lines.push(`${enchantmentLabel(id)} ${toRoman(Math.max(0, level - 1))}`)
    }
  }
  return lines.join(', ')
}


type IconStage = 'renderchest' | 'override' | 'item' | 'block' | 'derived' | 'fail'

const initialIconStage = (): IconStage => 'renderchest'

function ItemIcon({ id }: { id: string }) {
  const override = React.useMemo(() => overrideIcon(id), [id])
  const [stage, setStage] = React.useState<IconStage>(initialIconStage)

  React.useEffect(() => {
    setStage(initialIconStage())
  }, [id])

  if (stage === 'fail') {
    return <span className="mc-slot-fallback mono">{bareId(id).slice(0, 3).toUpperCase()}</span>
  }
  let src: string | null
  if (stage === 'renderchest') src = renderChestIconUrl(id)
  else if (stage === 'override') src = override?.url ?? null
  else if (stage === 'item') src = itemIconUrl(id)
  else if (stage === 'block') src = blockIconUrl(id)
  else src = derivedBlockIconUrl(id)
  if (src == null) {
    return <span className="mc-slot-fallback mono">{bareId(id).slice(0, 3).toUpperCase()}</span>
  }
  const nextStage = (cur: Exclude<IconStage, 'fail'>): IconStage => {
    if (cur === 'renderchest') return override ? 'override' : 'item'
    if (cur === 'override') return 'item'
    if (cur === 'item') return 'block'
    if (cur === 'block') return derivedBlockIconUrl(id) ? 'derived' : 'fail'
    return 'fail'
  }
  const onError = () => setStage(prev => prev === 'fail' ? 'fail' : nextStage(prev))
  if (stage === 'override' && override?.crop && override?.atlas) {
    const [cx, cy, cw, ch] = override.crop
    const [aw, ah] = override.atlas
    return (
      <svg
        className="mc-slot-img"
        viewBox={`${cx} ${cy} ${cw} ${ch}`}
        preserveAspectRatio="xMidYMid meet"
        overflow="hidden"
        style={{ overflow: 'hidden' }}
      >
        <image
          href={src}
          width={aw}
          height={ah}
          preserveAspectRatio="none"
          onError={onError}
        />
      </svg>
    )
  }
  return (
    <img
      className="mc-slot-img"
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={onError}
    />
  )
}


function DurabilityBar({ damage }: { damage: number }) {
  const pct = Math.max(0, Math.min(1, damage / 1500))
  const color = pct > 0.5 ? '#f43f5e' : pct > 0.25 ? '#f59e0b' : '#10b981'
  return (
    <div className="mc-slot-dur">
      <div className="mc-slot-dur-fill" style={{ width: `${(1 - pct) * 100}%`, background: color }} />
    </div>
  )
}


function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}
